import {
    CONFIRM_BOOKING,
    CONFIRM_BOOKING_SUCCESS,
    CONFIRM_BOOKING_FAILED,
    CLEAR_BOOKING
} from "../store/types";
import { RequestPushMsg, sendWorkflowNotification } from '../other/NotificationFunctions';
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { formatBookingObject, isFleetAdminPrivateClientBooking } from '../other/sharedFunctions';
import { buildScopedBookingUpdate } from '../other/bookingPrivacy';
import { callBookingTransaction } from '../other/bookingTransactionService';
import { child, get, onValue, push, runTransaction, query, orderByChild, equalTo, ref, update } from "firebase/database";
import { getBookingWorkflowForBookingRecord } from "../other/bookingWorkflow";

const SLOT_RULE_HISTORY_KEY = 'slotMovementHistory';
const SLOT_RULE_BOOKING_KEY = 'containerYardSlotRuleAdjustment';

const getBookingAddressText = (point) => {
    return point && (point.add || point.description || point.address) ? (point.add || point.description || point.address) : "";
};

const getPrivateBookingReference = (booking, bookingId) => booking.reference || booking.bookingNumber || bookingId;

const formatPrivateNotificationAmount = (value) => {
    const amount = parseFloat(value);
    return isNaN(amount) ? "0.00" : amount.toFixed(2);
};

const getFleetAdminNotificationName = (profile) => {
    if (!profile) return "Fleet Admin";
    return profile.companyName || profile.company_name || profile.businessName || profile.business_name ||
        [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim() || profile.email || "Fleet Admin";
};

const claimPrivateBookingNotification = async (bookingId, eventKey) => {
    let claimed = false;
    const markerRef = child(firebase.bookingPrivateRef(bookingId), `privateNotificationEvents/${eventKey}`);
    await runTransaction(markerRef, (current) => {
        if (current) return;
        claimed = true;
        return { sentAt: new Date().getTime() };
    });
    return claimed;
};

const notifyPrivateBookingCreated = async (booking, bookingId) => {
    if (!isFleetAdminPrivateClientBooking(booking)) return;
    const profile = store.getState().auth.profile || {};
    if (profile.usertype !== "fleetadmin") return;
    const claimed = await claimPrivateBookingNotification(bookingId, "created");
    if (!claimed) return;

    const reference = getPrivateBookingReference(booking, bookingId);
    const clientName = booking.clientName || booking.customer_name || "Private client";
    const pickup = getBookingAddressText(booking.pickup);
    const delivery = getBookingAddressText(booking.drop);
    const fleetMessage = `Private booking ${reference} was created for ${clientName}. ${pickup} to ${delivery}. ` +
        `Client charge: ${formatPrivateNotificationAmount(booking.truckingChargeToClient)}, ` +
        `commission: ${formatPrivateNotificationAmount(booking.superAdminCommissionAmount)}, ` +
        `fees: ${formatPrivateNotificationAmount(booking.totalAdditionalFeeAmount)}, ` +
        `net: ${formatPrivateNotificationAmount(booking.fleetAdminNetAmount)}.`;

    const notifications = [sendWorkflowNotification({
        uid: profile.uid,
        pushToken: profile.pushToken,
        title: "Private Client Booking Created",
        msg: fleetMessage,
        screen: "BookingHistory",
        params: { bookingId }
    })];

    const adminSnapshot = await get(query(firebase.usersRef, orderByChild("usertype"), equalTo("admin")));
    if (adminSnapshot.exists()) {
        const fleetAdminName = getFleetAdminNotificationName(profile);
        const adminMessage = `${fleetAdminName} created private booking ${reference} for ${clientName}. ` +
            `Client charge: ${formatPrivateNotificationAmount(booking.truckingChargeToClient)}, ` +
            `commission: ${formatPrivateNotificationAmount(booking.superAdminCommissionAmount)}, status: ${booking.status || "NEW"}.`;
        Object.entries(adminSnapshot.val()).forEach(([uid, adminProfile]) => {
            notifications.push(sendWorkflowNotification({
                uid,
                pushToken: adminProfile && adminProfile.pushToken,
                title: "New Fleet Admin Private Booking",
                msg: adminMessage,
                screen: "BookingHistory",
                params: { bookingId }
            }));
        });
    }
    await Promise.all(notifications);
};

const notifyFleetAdminsForBidOrder = async (booking, bookingId) => {
    try {
        if (!booking || booking.deliveryWithBid !== true) return;
        const snapshot = await get(query(firebase.usersRef, orderByChild("usertype"), equalTo("fleetadmin")));
        if (!snapshot.exists()) return;
        const fleetAdmins = snapshot.val();
        const vehicleType = booking.carType || "vehicle";
        const pickup = getBookingAddressText(booking.pickup);
        const delivery = getBookingAddressText(booking.drop);
        const msg = `A customer requested bids for ${vehicleType} from ${pickup} to ${delivery}.`;
        const notifications = Object.keys(fleetAdmins).map((uid) => {
            const fleetAdmin = fleetAdmins[uid];
            if (!fleetAdmin || fleetAdmin.active === false) return null;
            return sendWorkflowNotification({
                uid,
                pushToken: fleetAdmin.pushToken,
                title: "New Booking Order for Bid",
                msg,
                screen: "BookingOrdersForBid",
                params: { bookingId }
            });
        }).filter(Boolean);
        await Promise.all(notifications);
    } catch (error) {
        console.log(error);
    }
};

const BID_EXPIRATION_HOURS = 24;
const BID_EXPIRATION_REASON = "No accepted bid within 24 hours";

const hasValue = (value) => {
    if (value === undefined || value === null || value === false) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'object') {
        return Object.keys(value).some((key) => !!value[key]);
    }
    return true;
};

const removeBookingPreferenceField = (booking, field) => {
    if (Object.prototype.hasOwnProperty.call(booking, field)) {
        booking[field] = null;
    }
};

const cleanFleetOnlyBookingPayload = (booking) => {
    if (!booking) return booking;
    const fleetAdminId = booking.preferredFleetAdminId || booking.favoriteTranspoId || "";
    const fleetAdminName = booking.preferredFleetAdminName || booking.favoriteTranspoName || "";
    const hasFleetPreference = hasValue(fleetAdminId);
    const fleetFields = [
        "favoriteTranspoId",
        "favoriteTranspoName",
        "favoriteTranspoCompanyName",
        "favoriteTranspoType",
        "favoriteTranspoFleetAdmin",
        "preferredFleetAdminId",
        "preferredFleetAdminName"
    ];
    const driverFields = [
        "favoriteDriver",
        "favoriteDriverName",
        "favoriteDriverType",
        "favoriteTranspoDriver",
        "favoriteTranspoDriverName",
        "favoriteTranspoDriverCarType",
        "preferredDriverId",
        "preferredDriverName",
        "requestedDrivers",
        "preRequestedDrivers",
        "driverEstimates"
    ];

    if (hasFleetPreference) {
        booking.favoriteTranspoId = fleetAdminId;
        booking.preferredFleetAdminId = fleetAdminId;
        booking.favoriteTranspoName = fleetAdminName || "";
        booking.preferredFleetAdminName = fleetAdminName || "";
        booking.favoriteTranspoType = "fleetadmin";
        removeBookingPreferenceField(booking, "favoriteTranspoCompanyName");
        removeBookingPreferenceField(booking, "favoriteTranspoFleetAdmin");
        driverFields.forEach((field) => removeBookingPreferenceField(booking, field));
        return booking;
    }

    fleetFields.forEach((field) => removeBookingPreferenceField(booking, field));
    driverFields.forEach((field) => removeBookingPreferenceField(booking, field));
    return booking;
};

const CUSTOMER_CORE_SERVER_FIELDS = [
    'organizationId', 'organizationName', 'organizationClientRelationshipId', 'organizationClientNumber',
    'clientAuthUid', 'clientProfileId', 'fleetadmin', 'fleetadminUid', 'fleetAdminUid',
    'createdBy', 'createdByUid', 'createdByRole'
];

const constrainCustomerScopedCreate = (scoped, bookingId) => {
    const updates = { ...scoped.updates };
    const corePath = `bookings/${bookingId}`;
    const core = { ...(updates[corePath] || {}) };
    CUSTOMER_CORE_SERVER_FIELDS.forEach((field) => delete core[field]);
    updates[corePath] = core;
    delete updates[`bookingFinancials/${bookingId}`];
    Object.keys(updates).forEach((path) => {
        if (path.startsWith('organizationOwned/') || path.startsWith('fleetAdminOwned/') || path.startsWith('clientOwned/') || path.startsWith('driverAssigned/')) {
            delete updates[path];
        }
    });
    return { ...scoped, updates };
};
const addBidExpirationMetadata = (booking) => {
    if (!booking || booking.deliveryWithBid !== true || booking.status !== "NEW") return booking;
    const createdAt = typeof booking.bookingDate === "number" ? booking.bookingDate : new Date().getTime();
    booking.bidExpiresAt = createdAt + (BID_EXPIRATION_HOURS * 60 * 60 * 1000);
    booking.bidExpiresInHours = BID_EXPIRATION_HOURS;
    booking.bidExpirationStatus = "active";
    booking.autoCancelEligible = true;
    booking.bidExpirationReason = BID_EXPIRATION_REASON;
    return booking;
};

const normalizeText = (value) => {
    return typeof value === 'string' ? value.trim() : '';
};

const getFirstYardId = (value) => {
    if (Array.isArray(value)) {
        return value.map((item) => normalizeText(item)).find((item) => item) || '';
    }
    return normalizeText(value);
};

const getSlotRuleContext = (bookingData) => {
    const workflowConfigs = store.getState().workflowconfigs && store.getState().workflowconfigs.configs
        ? store.getState().workflowconfigs.configs
        : [];
    const workflow = getBookingWorkflowForBookingRecord(bookingData, workflowConfigs);
    const direction = normalizeText(workflow.slotDirection).toLowerCase();
    const slotAmount = Number(workflow.slotDelta !== undefined ? workflow.slotDelta : workflow.slotAmount) || 0;
    const workflowLog = {
        workflowSource: workflow.workflowSource || 'fallback',
        workflowConfigId: workflow.workflowConfigId || '',
        allowSlotRules: !!workflow.allowSlotRules,
        slotDirection: workflow.slotDirection || 'none',
        slotDelta: slotAmount
    };

    if (!workflow.allowSlotRules) {
        return {
            ...workflowLog,
            status: 'NO_ACTION_WORKFLOW_SLOT_RULE_DISABLED',
            shipmentCategory: workflow.shipmentType || '',
            containerSize: workflow.vehicleSize || ''
        };
    }

    if (direction === 'none') {
        return {
            ...workflowLog,
            status: 'NO_ACTION_WORKFLOW_SLOT_DIRECTION_NONE',
            shipmentCategory: workflow.shipmentType || '',
            containerSize: workflow.vehicleSize || ''
        };
    }

    if (direction !== 'add' && direction !== 'deduct') {
        return {
            ...workflowLog,
            status: 'SKIPPED_INVALID_WORKFLOW_SLOT_DIRECTION',
            shipmentCategory: workflow.shipmentType || '',
            containerSize: workflow.vehicleSize || ''
        };
    }

    if (slotAmount <= 0) {
        return {
            ...workflowLog,
            status: 'SKIPPED_INVALID_WORKFLOW_SLOT_AMOUNT',
            shipmentCategory: workflow.shipmentType || '',
            containerSize: workflow.vehicleSize || ''
        };
    }

    const shipmentCategory = workflow.shipmentType || '';
    const containerSize = workflow.vehicleSize || '';

    if (!shipmentCategory) {
        return {
            ...workflowLog,
            status: 'SKIPPED_MISSING_SHIPMENT_CATEGORY',
            shipmentCategory: '',
            containerSize
        };
    }

    if (!containerSize) {
        return {
            ...workflowLog,
            status: 'SKIPPED_MISSING_CONTAINER_SIZE',
            shipmentCategory,
            containerSize: ''
        };
    }

    const yardId = direction === 'add'
        ? (getFirstYardId(bookingData.pickupContainerYardId)
            || getFirstYardId(bookingData.emptyReturnContainerYardId)
            || getFirstYardId(bookingData.emptyReturnContainerYardIds))
        : (getFirstYardId(bookingData.emptyReturnContainerYardId)
            || getFirstYardId(bookingData.emptyReturnContainerYardIds)
            || getFirstYardId(bookingData.pickupContainerYardId));

    if (!yardId) {
        return {
            ...workflowLog,
            status: 'SKIPPED_MISSING_CONTAINER_YARD',
            shipmentCategory,
            containerSize
        };
    }

    return {
        ...workflowLog,
        status: 'READY',
        shipmentCategory,
        containerSize,
        yardId,
        delta: direction === 'add' ? slotAmount : -slotAmount,
        direction: direction.toUpperCase()
    };
};

const reserveBookingSlotRule = async (bookingKey, context) => {
    const { singleBookingRef } = firebase;
    const requestedAt = new Date().toISOString();
    const transactionResult = await runTransaction(singleBookingRef(bookingKey), (booking) => {
        if (!booking) return booking;
        if (booking[SLOT_RULE_BOOKING_KEY] && booking[SLOT_RULE_BOOKING_KEY].status) {
            return;
        }
        return {
            ...booking,
            [SLOT_RULE_BOOKING_KEY]: {
                shipmentCategory: context.shipmentCategory || '',
                containerSize: context.containerSize || '',
                yardId: context.yardId || '',
                direction: context.direction || '',
                requestedDelta: context.delta || 0,
                workflowSource: context.workflowSource || 'fallback',
                workflowConfigId: context.workflowConfigId || '',
                status: 'PROCESSING',
                requestedAt
            }
        };
    });
    return transactionResult.committed;
};

const finalizeBookingSlotRule = async (bookingKey, payload) => {
    const { singleBookingRef } = firebase;
    const updatedAt = new Date().toISOString();
    await runTransaction(singleBookingRef(bookingKey), (booking) => {
        if (!booking) return booking;
        return {
            ...booking,
            [SLOT_RULE_BOOKING_KEY]: {
                ...(booking[SLOT_RULE_BOOKING_KEY] || {}),
                ...payload,
                updatedAt
            }
        };
    });
};

const applyContainerYardSlotRule = async (bookingKey, bookingData) => {
    const { containerYardEditRef } = firebase;
    const context = getSlotRuleContext(bookingData);
    console.log('Booking workflow slot rule resolved', {
        bookingId: bookingKey,
        status: context.status,
        workflowSource: context.workflowSource || 'fallback',
        workflowConfigId: context.workflowConfigId || '',
        allowSlotRules: context.allowSlotRules === true,
        direction: context.direction || '',
        requestedDelta: context.delta || 0
    });

    const reserved = await reserveBookingSlotRule(bookingKey, context);
    if (!reserved) {
        return { status: 'DUPLICATE_IGNORED' };
    }

    if (context.status !== 'READY') {
        await finalizeBookingSlotRule(bookingKey, {
            status: context.status,
            applied: false,
            appliedDelta: 0,
            workflowSource: context.workflowSource || 'fallback',
            workflowConfigId: context.workflowConfigId || ''
        });
        return context;
    }

    const yardSnapshot = await get(containerYardEditRef(context.yardId));
    if (!yardSnapshot.exists()) {
        await finalizeBookingSlotRule(bookingKey, {
            status: 'SKIPPED_CONTAINER_YARD_NOT_FOUND',
            applied: false,
            appliedDelta: 0,
            workflowSource: context.workflowSource || 'fallback',
            workflowConfigId: context.workflowConfigId || ''
        });
        return {
            ...context,
            status: 'SKIPPED_CONTAINER_YARD_NOT_FOUND'
        };
    }

    const yardTransaction = await runTransaction(containerYardEditRef(context.yardId), (yard) => {
        if (!yard) {
            return yard;
        }
        const history = yard[SLOT_RULE_HISTORY_KEY] || {};
        if (history[bookingKey]) {
            return;
        }

        const currentSlots = yard.availableSlots !== undefined && !isNaN(Number(yard.availableSlots))
            ? Number(yard.availableSlots)
            : 0;
        const requestedNextSlots = currentSlots + context.delta;
        const blockedNegative = requestedNextSlots < 0;
        const appliedDelta = blockedNegative ? 0 : context.delta;
        const nextSlots = blockedNegative ? currentSlots : requestedNextSlots;
        const timestamp = new Date().toISOString();

        return {
            ...yard,
            availableSlots: nextSlots,
            [SLOT_RULE_HISTORY_KEY]: {
                ...history,
                [bookingKey]: {
                    bookingId: bookingKey,
                    shipmentCategory: context.shipmentCategory,
                    containerSize: context.containerSize,
                    yardId: context.yardId,
                    direction: context.direction,
                    requestedDelta: context.delta,
                    workflowSource: context.workflowSource || 'fallback',
                    workflowConfigId: context.workflowConfigId || '',
                    appliedDelta,
                    previousAvailableSlots: currentSlots,
                    nextAvailableSlots: nextSlots,
                    applied: !blockedNegative,
                    status: blockedNegative ? 'SKIPPED_INSUFFICIENT_SLOTS' : 'APPLIED',
                    updatedAt: timestamp
                }
            }
        };
    });

    const yardHistory = yardTransaction && yardTransaction.snapshot && yardTransaction.snapshot.val()
        ? yardTransaction.snapshot.val()[SLOT_RULE_HISTORY_KEY]
        : null;
    const appliedEntry = yardHistory ? yardHistory[bookingKey] : null;

    if (!appliedEntry) {
        await finalizeBookingSlotRule(bookingKey, {
            status: 'DUPLICATE_IGNORED',
            applied: false,
            appliedDelta: 0,
            workflowSource: context.workflowSource || 'fallback',
            workflowConfigId: context.workflowConfigId || ''
        });
        return {
            ...context,
            status: 'DUPLICATE_IGNORED'
        };
    }

    await finalizeBookingSlotRule(bookingKey, {
        status: appliedEntry.status,
        applied: appliedEntry.applied,
        appliedDelta: appliedEntry.appliedDelta,
        previousAvailableSlots: appliedEntry.previousAvailableSlots,
        nextAvailableSlots: appliedEntry.nextAvailableSlots,
        workflowSource: appliedEntry.workflowSource || context.workflowSource || 'fallback',
        workflowConfigId: appliedEntry.workflowConfigId || context.workflowConfigId || ''
    });

    return {
        ...context,
        status: appliedEntry.status,
        applied: appliedEntry.applied,
        appliedDelta: appliedEntry.appliedDelta
    };
};

export const clearBooking = () => (dispatch) => {
    dispatch({
        type: CLEAR_BOOKING,
        payload: null,
    });
}

export const addBooking = (bookingData) => async (dispatch) => {

    const   {
        bookingRef,
        rootRef,
        settingsRef,
        singleUserRef
    } = firebase;

    dispatch({
        type: CONFIRM_BOOKING,
        payload: bookingData,
    });

    const settingsdata = await get(settingsRef);
    const settings = settingsdata.val();

    let data = await formatBookingObject(bookingData, settings);
    data = cleanFleetOnlyBookingPayload(data);
    data = addBidExpirationMetadata(data);

    if(data.requestedDrivers){
        const drivers = data.requestedDrivers;
        Object.keys(drivers).map((uid)=>{
            onValue(singleUserRef(uid),  snapshot => {
                if (snapshot.val()) {
                    const pushToken = snapshot.val().pushToken;
                    const ios = snapshot.val().userPlatform == "IOS"? true: false
                    if(pushToken){
                        RequestPushMsg(
                            pushToken,
                            {
                                title: store.getState().languagedata.defaultLanguage.notification_title,
                                msg: store.getState().languagedata.defaultLanguage.new_booking_notification,
                                screen: 'DriverTrips'
                            });
                     }
                 }
            }, {onlyOnce: true});
            return drivers[uid];
        })
    }

    try {
        const bookingKey = push(bookingRef).key;
        const fullBooking = { ...data, id: bookingKey, bookingId: bookingKey };
        const actorType = store.getState().auth.profile && store.getState().auth.profile.usertype;
        if (actorType === 'fleetadmin') {
            const result = await callBookingTransaction('fleet_admin_create_booking', {
                bookingId: bookingKey,
                booking: fullBooking
            });
            const serverBooking = result.booking || fullBooking;
            applyContainerYardSlotRule(bookingKey, serverBooking);
            dispatch({
                type: CONFIRM_BOOKING_SUCCESS,
                payload: {
                    booking_id: bookingKey,
                    mainData: serverBooking
                }
            });
            try {
                await notifyPrivateBookingCreated(serverBooking, bookingKey);
            } catch (error) {
                console.log("Private booking created notification failed", error);
            }
            notifyFleetAdminsForBidOrder(serverBooking, bookingKey);
            return result;
        }
        const initialScoped = buildScopedBookingUpdate(bookingKey, fullBooking, {
          includeIndexes: actorType !== 'customer',
          omitProtectedCoreOwnership: actorType === 'customer'
        });
        const scoped = actorType === 'customer' ? constrainCustomerScopedCreate(initialScoped, bookingKey) : initialScoped;
        await update(rootRef || ref(firebase.database), scoped.updates);
        applyContainerYardSlotRule(bookingKey, fullBooking);
        dispatch({
            type: CONFIRM_BOOKING_SUCCESS,
            payload: {
                booking_id: bookingKey,
                mainData: fullBooking
            }
        });
        try {
            await notifyPrivateBookingCreated(fullBooking, bookingKey);
        } catch (error) {
            console.log("Private booking created notification failed", error);
        }
        notifyFleetAdminsForBidOrder(fullBooking, bookingKey);
    } catch (error) {
        dispatch({
            type: CONFIRM_BOOKING_FAILED,
            payload: error.code + ": " + error.message,
        });
    }
};
